import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { OnboardingStage, VerificationStatus } from '@/types';
import type { AdminOnboardingItem } from '@/features/admin/api/admin.api';
import { LEGACY_RECORD_REASON } from '@/features/admin/constants/onboardingStages';
import {
  columnsForStage,
  type OnboardingColumn,
} from '@/features/admin/components/onboardingColumns';

/**
 * The pipeline table. Replaces the near-identical scaffolding that the
 * verification queue and the profile review queue each carried their own copy
 * of.
 *
 * The chrome — header row and pagination — renders in every state. An earlier
 * version returned early for loading and empty, which made the pagination
 * control vanish on each tab switch and refetch and took the table landmark
 * away from screen readers along with it.
 */

interface GuideOnboardingTableProps {
  stage: OnboardingStage;
  rows: AdminOnboardingItem[];
  isLoading: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Non-empty when a search is narrowing the list; changes the empty copy. */
  search?: string;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onOpen: (row: AdminOnboardingItem) => void;
  onAction: (row: AdminOnboardingItem, action: RowActionKey) => void;
  /** The row with a decision in flight, so it can show a spinner. */
  pendingRowId?: string;
  /** Any decision in flight, which disables every other row's action. */
  isActionPending?: boolean;
  /** Escape hatch for tests and future tabs. */
  columns?: OnboardingColumn[];
}

/** An action offered on a row, beyond the always-present "View". */
export interface RowAction {
  key: RowActionKey;
  label: string;
  color?: 'primary' | 'success' | 'error';
  disabledReason?: string;
  /** Opens the record instead of mutating it, so it stays live while saving. */
  isNavigation?: boolean;
}

export type RowActionKey = 'send' | 'pass' | 'fail' | 'retry' | 'review';

/**
 * What can be done to this row right now, or nothing when the ball is in
 * someone else's court.
 */
function resolveRowActions(row: AdminOnboardingItem): RowAction[] {
  switch (row.stage) {
    case OnboardingStage.BACKGROUND_CHECK:
      // Sending is now the bulk CSV export, so the only decision left on a row
      // is recording the vendor's verdict. A failed check is recorded from the
      // guide's review page, which still carries the notes dialog.
      return [
        {
          key: 'pass',
          label: 'Verified by SpringVerify',
          color: 'success',
          // PENDING is deliberately actionable: `pass` is legal from it, and
          // anyone sent in an earlier manual batch would otherwise be stranded.
          disabledReason:
            row.lifecycle.verificationStatus === VerificationStatus.NOT_STARTED
              ? LEGACY_RECORD_REASON
              : undefined,
        },
      ];
    case OnboardingStage.PROFILE_REVIEW:
      return [{ key: 'review', label: 'Review', isNavigation: true }];
    case OnboardingStage.REJECTED:
      return [{ key: 'retry', label: 'Re-open' }];
    default:
      return [];
  }
}

export function GuideOnboardingTable({
  stage,
  rows,
  isLoading,
  isFetching,
  isError,
  onRetry,
  search,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onOpen,
  onAction,
  pendingRowId,
  isActionPending,
  columns: columnsOverride,
}: GuideOnboardingTableProps) {
  const columns = columnsOverride ?? columnsForStage(stage);

  const renderActions = (row: AdminOnboardingItem) => {
    const actions = resolveRowActions(row);
    if (actions.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {actions.map((action) => {
          const isThisRowPending = pendingRowId === row.id;
          const disabled =
            Boolean(action.disabledReason) ||
            (!action.isNavigation && Boolean(isActionPending));
          const reasonId = action.disabledReason
            ? `${row.id}-${action.key}-reason`
            : undefined;

          return (
            <Tooltip key={action.key} title={action.disabledReason ?? ''}>
              {/* A disabled button swallows pointer events, so the tooltip
                  needs a wrapper to hang off. */}
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color={action.color ?? 'primary'}
                  disabled={disabled}
                  aria-describedby={reasonId}
                  onClick={() => onAction(row, action.key)}
                  startIcon={
                    isThisRowPending ? <CircularProgress size={14} color="inherit" /> : undefined
                  }
                >
                  {isThisRowPending ? 'Saving…' : action.label}
                </Button>
                {/* A tooltip alone is not announced on a disabled control. */}
                {reasonId && (
                  <Box component="span" id={reasonId} sx={visuallyHidden}>
                    {action.disabledReason}
                  </Box>
                )}
              </span>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const body = () => {
    if (isLoading) {
      return Array.from({ length: Math.min(rowsPerPage, 5) }, (_, index) => (
        <TableRow key={`skeleton-${index}`} aria-hidden>
          {columns.map((column) => (
            <TableCell key={column.id}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (isError) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              We couldn&apos;t load this tab.
            </Typography>
            {onRetry && (
              <Button variant="outlined" size="small" onClick={onRetry}>
                Retry
              </Button>
            )}
          </TableCell>
        </TableRow>
      );
    }

    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {search
                ? `No applicants match "${search}" in this tab.`
                : 'Nothing in this tab right now.'}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => (
      <TableRow
        key={row.id}
        hover
        sx={{
          opacity:
            row.stage === OnboardingStage.REJECTED || pendingRowId === row.id ? 0.65 : 1,
        }}
      >
        {columns.map((column) => (
          <TableCell key={column.id} align={column.align ?? 'left'}>
            {column.render(row, { onOpen, renderActions })}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // Clamp, so a search that shrinks the result set cannot leave the control on
  // a page that no longer exists.
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);

  return (
    <>
      {/* Fixed height, so appearing mid-refetch does not shift the table. */}
      <Box sx={{ height: 4 }}>
        {isFetching && !isLoading && <LinearProgress sx={{ height: 4 }} />}
      </Box>

      <TableContainer>
        <Table aria-label="Guide onboarding applicants">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align ?? 'left'}>
                  {column.id === 'actions' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {column.header}
                    </Box>
                  ) : (
                    column.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{body()}</TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.min(page, lastPage)}
        onPageChange={(_, value) => onPageChange(value)}
        rowsPerPage={rowsPerPage}
        // Capped at the server's own `limit` ceiling, so the rows and the
        // reported total cannot disagree.
        rowsPerPageOptions={[10, 20, 50, 100]}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
      />
    </>
  );
}

export { resolveRowActions };
